import { RecoilRoot } from "recoil";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing";
import React, { useMemo } from "react";
import { createStackNavigator } from "@react-navigation/stack";
import { ContractSignatureStatus, ProfileType } from "../../__generated__/graphql";

import { GET_CONTRACT } from "../../graphql/core";
import { buildAsset, buildContract, buildMissionInfo, buildUser } from "../factories";
import type { Meta, StoryObj } from "@storybook/react";
import { MockedResponse } from "@apollo/client/testing/core";
import { Button, View } from "react-native";
import Callout from "../../ui_atoms/Callout";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import ContractPreviewScreen from "../../screens/user/ContractPreviewScreen";
import { GET_ASSET } from "../../graphql/asset";
import { StackScreenProps } from "@react-navigation/stack";

type StoryStackParams = {
    Home: undefined;
    ContractPreviewScreen: { contractId: string };
    MissionSelectSignerScreen: { missionId: string };
    ContractSigningScreen: { contractId: string };
};

const HomeFakeScreen: React.FC<StackScreenProps<StoryStackParams, "Home">> = ({ navigation }) => {
    return (
        <View>
            <Callout markdown="Home screen" />
            <Button
                title="Back to preview"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "ContractPreviewScreen",
                                params: { contractId: "test-contract-need_self_sign" },
                            },
                        ],
                    });
                }}
            />
        </View>
    );
};

const DelegateSigner: React.FC<StackScreenProps<StoryStackParams, "MissionSelectSignerScreen">> = ({
    navigation,
    route,
}) => {
    return (
        <View>
            <Callout markdown={`Select Signer: ${route.params.missionId}`} />
            <Button
                title="Back to preview"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "ContractPreviewScreen",
                                params: { contractId: "test-contract-need_self_sign" },
                            },
                        ],
                    });
                }}
            />
        </View>
    );
};

const SignScreen: React.FC<StackScreenProps<StoryStackParams, "ContractSigningScreen">> = ({
    navigation,
    route,
}) => {
    return (
        <View>
            <Callout markdown={`Sign: ${route.params.contractId}`} />
            <Button
                title="Back to preview"
                onPress={() => {
                    navigation.reset({
                        index: 0,
                        routes: [
                            {
                                name: "ContractPreviewScreen",
                                params: { contractId: "test-contract-need_self_sign" },
                            },
                        ],
                    });
                }}
            />
        </View>
    );
};

const Stack = createStackNavigator<StoryStackParams>();

const StoryComponent = ({ profileType }: { profileType: ProfileType }) => {
    const user = useMemo(buildUser, []);
    const mock = useMemo(
        () =>
            Object.values(ContractSignatureStatus)
                .reduce((acc, state) => {
                    const contractId = `test-contract-${state}`;
                    acc.push({
                        request: {
                            query: GET_CONTRACT,
                            variables: {
                                id: contractId,
                            },
                        },
                        newData: () => {
                            return {
                                data: {
                                    getContract: {
                                        ...buildContract({
                                            id: contractId,
                                            signatureStatus: state,
                                            contractEffectiveAsset: buildAsset(),
                                        }),
                                        ...{ missionInfo: buildMissionInfo() },
                                    },
                                },
                            };
                        },
                    });
                    return acc;
                }, [] as MockedResponse[])
                .concat([
                    {
                        variableMatcher: () => true,
                        request: {
                            query: GET_ASSET,
                        },
                        newData: () => {
                            return {
                                data: {
                                    getAsset: buildAsset(),
                                },
                            };
                        },
                    },
                ]),
        [],
    );

    return (
        <RecoilRoot>
            <MockedAuthManager user={user}>
                <MockedProfileSwitcherManager profileType={profileType}>
                    <MockedProvider mocks={mock}>
                        <Stack.Navigator initialRouteName="ContractPreviewScreen">
                            <Stack.Screen
                                name="ContractPreviewScreen"
                                component={ContractPreviewScreen}
                                initialParams={{ contractId: `test-contract-need_self_sign` }}
                                options={{
                                    gestureEnabled: false,
                                    headerShown: true,
                                    title: "",
                                    headerBackTitle: "Back",
                                    headerBackButtonDisplayMode: "default",
                                }}
                            />
                            <Stack.Screen name="Home" component={HomeFakeScreen} />
                            <Stack.Screen
                                name="MissionSelectSignerScreen"
                                component={DelegateSigner}
                            />
                            <Stack.Screen name="ContractSigningScreen" component={SignScreen} />
                        </Stack.Navigator>
                    </MockedProvider>
                </MockedProfileSwitcherManager>
            </MockedAuthManager>
        </RecoilRoot>
    );
};

const meta = {
    title: "Screens/Contract preview",
    component: StoryComponent,
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
    },
};

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
    },
};
